import { useState, useMemo } from 'react'
import type { AlbumConfig, StickerInvestment, ThemeDefinition } from '../types'
import { formatCurrency, stickerTitle, stickerImage, stickerValue } from '../data'
import { useI18n } from '../i18n/context'

const STICKERS_PER_PAGE = 9

interface Props {
  stickerNumbers: number[]
  stickers: Record<number, StickerInvestment>
  theme: ThemeDefinition
  album: AlbumConfig
  onStickerClick: (stickerNumber: number) => void
}

export function FlipBook({ stickerNumbers, stickers, theme, album, onStickerClick }: Props): JSX.Element {
  const { t } = useI18n()
  const pages = useMemo(() => {
    const result: number[][] = []
    for (let i = 0; i < stickerNumbers.length; i += STICKERS_PER_PAGE) {
      result.push(stickerNumbers.slice(i, i + STICKERS_PER_PAGE))
    }
    return result
  }, [stickerNumbers])

  const [currentPage, setCurrentPage] = useState(0)
  const [flipping, setFlipping] = useState<'left' | 'right' | null>(null)

  function goTo(next: number): void {
    if (next < 0 || next >= pages.length || flipping) return
    const direction = next > currentPage ? 'right' : 'left'
    setFlipping(direction)
    setTimeout(() => { setCurrentPage(next); setFlipping(null) }, 320)
  }

  const page = pages[currentPage] ?? []
  const totalPages = pages.length

  return (
    <div className="flipbook">
      <div className="flipbook-controls">
        <button
          className="ghost-button flipbook-arrow"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage === 0 || flipping !== null}
          type="button"
          aria-label={t.previousPage}
        >‹</button>
        <span className="flipbook-page-indicator">
          {t.pageOf(currentPage + 1, totalPages)}
          <small> ({t.stickersRange((currentPage * STICKERS_PER_PAGE) + 1, Math.min((currentPage + 1) * STICKERS_PER_PAGE, stickerNumbers.length))})</small>
        </span>
        <button
          className="ghost-button flipbook-arrow"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage === totalPages - 1 || flipping !== null}
          type="button"
          aria-label={t.nextPage}
        >›</button>
      </div>

      <div className={`flipbook-spread ${flipping ? `is-flipping-${flipping}` : ''}`}>
        {page.map((stickerNumber) => {
          const investment = stickers[stickerNumber]
          const imageUrl = stickerImage(theme, stickerNumber)
          const title = stickerTitle(theme, stickerNumber)
          const value = stickerValue(stickerNumber, album.size)

          return (
            <button
              key={stickerNumber}
              className={`flipbook-sticker ${investment ? 'is-collected' : ''}`}
              onClick={() => onStickerClick(stickerNumber)}
              type="button"
            >
              <div className="flipbook-sticker-inner">
                {!investment ? (
                  /* Not collected: mystery card with big number */
                  <div className="flipbook-mystery">
                    <span className="flipbook-mystery-num">{stickerNumber}</span>
                    <span className="flipbook-mystery-value">{formatCurrency(value, album.currency)}</span>
                    <span className="flipbook-mystery-label">{t.openSlot}</span>
                  </div>
                ) : (
                  /* Collected: show art + details */
                  <>
                    <div className="flipbook-sticker-front">
                      {imageUrl ? (
                        <img className="flipbook-art" src={imageUrl} alt={title} loading="lazy" />
                      ) : (
                        <div className="flipbook-art-placeholder"><span>{title}</span></div>
                      )}
                      <span className="flipbook-number">#{stickerNumber}</span>
                    </div>
                    <div className="flipbook-sticker-back">
                      <span className="flipbook-value">{formatCurrency(value, album.currency)}</span>
                      <span className="flipbook-status">✓ {t.collected}</span>
                      <span className="flipbook-date">
                        {new Date(investment.investedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </button>
          )
        })}

        {page.length < STICKERS_PER_PAGE &&
          Array.from({ length: STICKERS_PER_PAGE - page.length }).map((_, i) => (
            <div key={`empty-${i}`} className="flipbook-sticker is-empty" />
          ))}
      </div>

      <div className="flipbook-jump">
        <label className="field">
          <span>{t.jumpToPage}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={currentPage + 1}
            onChange={(e) => {
              const v = Math.max(1, Math.min(totalPages, Number(e.target.value)))
              goTo(v - 1)
            }}
            style={{ width: '80px' }}
          />
        </label>
      </div>
    </div>
  )
}
